// What did the user actually give us?
//
// Stage 3 lets three different things land in the same box: a meal idea, a
// link, and a whole recipe pasted out of somewhere else. They need completely
// different handling, so something has to tell them apart before anything else
// runs.
//
// This is NOT a mock and has no SWAP POINT. It reads text the user typed; no
// real integration changes that. Pure and synchronous, like lib/matchRecipes.

import { isUrl } from './matchRecipes.js';

export const ROUTES = ['url', 'recipeText', 'idea'];

// Words that mean "an amount follows" even without a digit in front.
const UNIT_WORDS = new Set([
  'cup', 'cups', 'tbsp', 'tablespoon', 'tablespoons', 'tsp', 'teaspoon',
  'teaspoons', 'oz', 'ounce', 'ounces', 'lb', 'lbs', 'pound', 'pounds', 'g',
  'gram', 'grams', 'kg', 'ml', 'l', 'litre', 'litres', 'liter', 'liters',
  'pinch', 'dash', 'clove', 'cloves', 'slice', 'slices', 'can', 'cans',
  'handful', 'bunch', 'sprig', 'sprigs', 'stick', 'sticks',
]);

const NUMBER_WORDS = new Set([
  'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'half', 'quarter', 'a', 'an',
]);

// The two Unicode fraction ranges, spelled out rather than pasted, so the
// class stays readable and can't be mangled by an encoding round-trip.
const FRACTIONS = '\\u00BC-\\u00BE\\u2150-\\u215E';

// A heading like "Ingredients:" or "For the sauce" is the strongest single
// signal that this is a recipe rather than a request for one.
const SECTION_HEADING = /^\s*(ingredients?|instructions?|directions?|method|steps?|you(?:'| wi)?ll need)\s*:?\s*$/i;

// A line that opens with an amount: "2 cups flour", "½ tsp salt",
// "- 1 lb beef", "3 cloves garlic". Bullets and dashes are stripped first
// because pasted recipes are full of them.
export function looksLikeQuantityLine(line) {
  const text = String(line || '').replace(/^\s*[-*•‣◦⁃]\s*/, '').trim();
  if (!text) return false;

  // Needs something after the amount — a bare "2" is not an ingredient line.
  if (new RegExp(`^(\\d+([.,/]\\d+)?|[${FRACTIONS}])`).test(text)) {
    return /\s+\S/.test(text);
  }

  const [first, second] = text.toLowerCase().split(/\s+/);
  if (!second) return false;
  if (UNIT_WORDS.has(first.replace(/[.,]$/, ''))) return true;
  // "a pinch of salt" — a number word only counts when a unit follows it,
  // otherwise every sentence starting with "A" or "An" would qualify.
  return NUMBER_WORDS.has(first) && UNIT_WORDS.has(second.replace(/[.,]$/, ''));
}

// Deliberately conservative. A false "this is a recipe" is worse than a false
// "this is an idea": mistaking a pasted recipe for a search still finds
// something, while mistaking a two-word idea for a recipe produces nonsense.
// The override exists for whichever way it gets it wrong.
export function classifyInput(input) {
  const raw = String(input || '').trim();
  if (!raw) return 'idea';
  if (isUrl(raw)) return 'url';

  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return 'idea';

  if (lines.some((line) => SECTION_HEADING.test(line))) return 'recipeText';

  const quantityLines = lines.filter(looksLikeQuantityLine).length;
  return quantityLines >= 2 ? 'recipeText' : 'idea';
}

// The user's correction wins over the guess, but only while it still makes
// sense: you cannot force a link to be read as prose, and pasting a link over
// a correction should re-guess rather than inherit the old one.
export function resolveRoute(input, override = null) {
  const guess = classifyInput(input);
  if (!override || override === guess) return guess;
  if (guess === 'url' || override === 'url') return guess;
  return ROUTES.includes(override) ? override : guess;
}

// What the other reading would be, so the UI can name it: "treat as a meal
// idea instead". Null when there is no sensible alternative.
export function alternateRoute(route) {
  if (route === 'idea') return 'recipeText';
  if (route === 'recipeText') return 'idea';
  return null;
}

export const ROUTE_LABELS = {
  url: 'a link',
  recipeText: 'a pasted recipe',
  idea: 'a meal idea',
};
